import { Loader2, Sparkles } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useSessionInvocations } from '@/features/skills/use-skills'

const eurFormatter = new Intl.NumberFormat('de-CH', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
})

interface SkillInvocationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessionId: string | null
  skillName: string
  /** Erstellungszeit der zugehoerigen User-Message — wir matchen die naechste Invocation danach. */
  anchorCreatedAt: string
}

export function SkillInvocationModal({
  open,
  onOpenChange,
  sessionId,
  skillName,
  anchorCreatedAt,
}: SkillInvocationModalProps) {
  const { data, isLoading } = useSessionInvocations(open ? sessionId : null)

  // Naechste Invocation fuer diesen skill_name, die NACH oder AM anchor angelegt wurde.
  // Server liefert desc, wir suchen die zeitlich naechste.
  const invocation = data?.invocations
    .filter((i) => i.skill_name === skillName)
    .find((i) => new Date(i.created_at).getTime() >= new Date(anchorCreatedAt).getTime() - 1000)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent" />
            <span className="font-mono">/{skillName}</span>
          </DialogTitle>
          <DialogDescription>Skill-Ausführung</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : !invocation ? (
          <div className="py-4 text-sm text-muted-foreground">
            Keine Ausführungsdaten gefunden.
          </div>
        ) : (
          <dl className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Operation</dt>
            <dd className="font-mono">{invocation.operation ?? '—'}</dd>

            <dt className="text-muted-foreground">Status</dt>
            <dd>{invocation.success ? 'Erfolgreich' : `Fehlgeschlagen: ${invocation.error_message ?? ''}`}</dd>

            <dt className="text-muted-foreground">Eingabe</dt>
            <dd className="break-all">{invocation.input_summary ?? '—'}</dd>

            <dt className="text-muted-foreground">Ausgabe</dt>
            <dd className="line-clamp-4 whitespace-pre-wrap">{invocation.output_summary ?? '—'}</dd>

            <dt className="text-muted-foreground">Dauer</dt>
            <dd>{invocation.duration_ms != null ? `${(invocation.duration_ms / 1000).toFixed(1)} s` : '—'}</dd>

            <dt className="text-muted-foreground">Tokens</dt>
            <dd>{invocation.tokens_consumed ?? '—'}</dd>

            <dt className="text-muted-foreground">Kosten</dt>
            <dd>
              {invocation.cost_eur != null && invocation.cost_eur > 0
                ? eurFormatter.format(invocation.cost_eur)
                : '—'}
            </dd>
          </dl>
        )}
      </DialogContent>
    </Dialog>
  )
}
